import { PageController } from '@client/components/_core/layout/PageController';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { message } from '@client/components/_ui/feedback/message';
import { useAdminModulePermissions } from '@client/hooks/admin/useAdminModulePermissions';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import i18n from '@client/i18n/config';
import { createFileRoute, Navigate } from '@tanstack/react-router';
import { useState } from 'react';
import { match } from 'ts-pattern';
import { type AppText, AppTextForm } from './AppTextForm';

export const Route = createFileRoute('/admin-console/platform-content/comms-and-content/app-text/add')({
    component: AddAppTextRoute,
    staticData: {
        presentation: 'overlay',
    },
});

function AddAppTextRoute() {
    const { isReadOnly } = useAdminModulePermissions('comms');

    if (isReadOnly) {
        return <Navigate to="/admin-console/platform-content/comms-and-content/app-text" />;
    }

    return (
        <PageController route={Route}>
            <AddAppText />
        </PageController>
    );
}

function AddAppText() {
    const { trpc, trpcUtils } = useTrpc();
    const [open, setOpen] = useState(true);
    const navigate = useNavigate();
    const { mutate: createAppText, isPending: isCreating } = trpc.admin.appText.createAppText.useMutation({
        onError: (error) => {
            errorMessage.show(error);
        },
    });

    const handleSubmit = (values: AppText) => {
        const key = 'create_text';
        message.loading({ content: 'Creating…', key });

        createAppText(
            {
                key: values.key ?? '',
                value: values.value ?? '',
                locale: values.locale ?? 'en-GB',
                namespace: values.namespace ?? 'translation',
                description: values.description,
                format: values.format ?? 'PLAIN_TEXT',
            },
            {
                onSuccess: () => {
                    message.success({ content: 'Created', key });

                    trpcUtils.admin.appText.listAppTexts.invalidate();

                    const namespace = values.namespace ?? 'translation';
                    void i18n.reloadResources(undefined, namespace);

                    setOpen(false);
                },
                onError: (error) => {
                    message.error(error.message || 'Failed to create app text');
                },
            },
        );
    };

    return (
        <AppTextForm
            open={open}
            mode="add"
            data={undefined}
            state={match({ isCreating })
                .with({ isCreating: true }, () => 'submitting' as const)
                .otherwise(() => 'ready' as const)}
            onSubmit={handleSubmit}
            onCloseFinished={() => {
                navigate({ to: '/admin-console/platform-content/comms-and-content/app-text' });
            }}
        />
    );
}
